'use strict';

let timeouts    = [];
let messageName = "zero-timeout-message";

// INFO: https://dbaron.org/log/20100309-faster-timeouts
/**
 * Calls a function in an async manner in as short a timespan as possible
 * @param {function} fn
 */
let setZeroTimeout = (fn) => {
    timeouts.push(fn);
    window.postMessage(messageName, "*");
}

/**
 * Handles postMessages sent to the current window
 * @param {MessageEvent} event
 */
let handleZeroTimeoutMessage = (event) => {
    if (event.source === window && event.data === messageName) {
        event.stopPropagation();

        timeouts.length && timeouts.shift()();
    }
}

if (!window.setZeroTimeout) {
    window.setZeroTimeout = setZeroTimeout;
    window.addEventListener("message", handleZeroTimeoutMessage, true);
}

let uniqueCounter = 0;
let newId         = (prefix = 'id_') => prefix + ++uniqueCounter;
let isObject      = (obj)  => !!(obj && typeof obj === 'object' && !Array.isArray(obj) && obj.contructor !== Date);
let isArray       = (obj)  => Array.isArray(obj);
let camelize      = (str)  => str.replace(/-./g, x=>x[1].toUpperCase());
let kebabelize    = (str)  => str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
let getIsoDateStr = (date) => new Date(date.getTime() - (date.getTimezoneOffset()*60*1000)).toISOString().split('T')[0]; // INFO: https://stackoverflow.com/questions/23593052/format-javascript-date-as-yyyy-mm-dd

if (!window.queueMicrotask) {
    window.queueMicrotask = (fn) => Promise.resolve().then(fn);
}

/**
 * Define a get/set property on an object
 * @param {object} obj the object
 * @param {string} key the property name
 * @param {any} initialValue the intial value of the property
 * @param {function} callbackOnUpdatedValue
 */
 let defineProperty = (obj, key, initialValue, callbackOnUpdatedValue) => {
    let propName = '$$prop$$' + key.toLowerCase();

    obj[propName] = initialValue;

    Object.defineProperty(obj, key, {
        configurable: true, // Allow delete of property
        enumerable  : true,
        get         : () => obj[propName],
        set(value) {
            let oldValue = obj[propName];

            if (oldValue !== value) {
                obj[propName] = value;
                callbackOnUpdatedValue?.(key, oldValue, value);
            }
        }
    });
}

/**
 * Define a get/set property on an object
 * @param {object} obj the object
 * @param {string} key the property name
 * @param {any} value the value of the property
 */
 let defineReadOnlyProperty = (obj, key, value) => {
    Object.defineProperty(obj, key, {
        configurable: true,
        enumerable  : true,
        get         : () => value
    });
}

// INFO: https://stackoverflow.com/questions/21485545/is-there-a-way-to-tell-if-an-es6-promise-is-fulfilled-rejected-resolved
let makeQueryablePromise = (promise) => {
    if (promise.isResolved) {
        return promise;
    }

    let isResolved = false;
    let isRejected = false;

    let result = promise.then(v => { isResolved = true; return v; }, e => { isRejected = true; throw e; });

    result.isSettled  = () => isResolved || isRejected;
    result.isResolved = () => isResolved;
    result.isRejected = () => isRejected;

    return result;
}

/**
 * Try parsing a string as a json object
 * @param {string} str the string to be parsed
 * @returns {Object|Array|String} the parsed object or undefined if unsuccessful
 */
 let tryParse = (str) => {
    try {
        return JSON.parse(str);
    } catch (e) {}
}

let clone = (src) => {
    if (isArray(src)) {
        return src.map((item) => isArray(item) || isObject(item) ? clone(item) : item);
    }
    else if (!isObject (src)) {
        return src;
    }

    const cloned = Object.assign({}, src);

    Object.keys(cloned).forEach(key => {
        const value = cloned[key];

        if (value) {
            if (isArray(value) || isObject(value)) {
                cloned[key] = clone(value);
            }
        }
    });

    return cloned;
}

export { isArray, isObject, defineProperty, defineReadOnlyProperty, tryParse, camelize, kebabelize, newId, getIsoDateStr, clone };